import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { X, Info, AlertTriangle, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface Announcement {
  id: string;
  title: string;
  content: string;
  type: string;
  is_dismissible: boolean;
}

const DISMISSED_KEY = "dismissed_announcements";

const typeStyles: Record<string, { bg: string; border: string; text: string; icon: React.ElementType }> = {
  info: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-800", icon: Info },
  warning: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800", icon: AlertTriangle },
  maintenance: { bg: "bg-red-50", border: "border-red-200", text: "text-red-800", icon: Wrench },
};

export function PlatformAnnouncementBanner() {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [dismissed, setDismissed] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(DISMISSED_KEY) ?? "[]");
    } catch {
      return [];
    }
  });

  useEffect(() => {
    supabase
      .from("platform_announcements")
      .select("id, title, content, type, is_dismissible")
      .order("created_at", { ascending: false })
      .then(({ data }) => {
        if (data) setAnnouncements(data as Announcement[]);
      });
  }, []);

  const dismiss = (id: string) => {
    const next = [...dismissed, id];
    setDismissed(next);
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(next));
  };

  const visible = announcements.filter((a) => !dismissed.includes(a.id));
  if (visible.length === 0) return null;

  const a = visible[0];
  const style = typeStyles[a.type] ?? typeStyles.info;
  const Icon = style.icon;

  return (
    <div className={cn("flex items-center gap-3 px-4 py-2 border-b", style.bg, style.border, style.text)}>
      <Icon className="h-4 w-4 shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="font-medium text-sm">{a.title}</span>
        {a.content && <span className="text-sm ml-2 opacity-80">— {a.content}</span>}
      </div>
      {a.is_dismissible && (
        <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={() => dismiss(a.id)}>
          <X className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}
